import React from "react";
import { nanoid } from "nanoid";
import { useTranslation } from "react-i18next";
import {
  Stack,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";

export type AgeRange = {
  id: string;
  lower: number | null;
  upper: number | null;
};

const AgeRangeField = ({
  ageRangeRows,
  setAgeRangeRows,
}: {
  ageRangeRows: AgeRange[];
  setAgeRangeRows: React.Dispatch<React.SetStateAction<AgeRange[]>>;
}) => {
  const { t } = useTranslation("questEditor");
  const [lower, setLower] = React.useState<string>("");
  const [upper, setUpper] = React.useState<string>("");

  const handleRemoveAgeRange = (id: string) => {
    setAgeRangeRows((prev: AgeRange[]) => prev.filter((row) => row.id !== id));
  };

  const handleAddAgeRange = () => {
    const newRow: AgeRange = {
      id: nanoid(),
      lower: lower === "" ? null : Number(lower),
      upper: upper === "" ? null : Number(upper),
    };
    setAgeRangeRows((prev: AgeRange[]) =>
      prev.includes(newRow) ? prev : [...prev, newRow],
    );
    setLower("");
    setUpper("");
  };

  return (
    <Accordion
      sx={{
        ".MuiAccordionSummary-root .MuiSvgIcon-root": {
          transform: "rotate(0deg)",
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {ageRangeRows.length > 0 ? (
          <Typography color="primary">
            {ageRangeRows
              .map((row) =>
                t("quest_edit_target.field.basic_attr.age.label", {
                  lower: row.lower,
                  upper: row.upper,
                }),
              )
              .join(", ")}
          </Typography>
        ) : (
          <Typography color="primary">
            {t("quest_edit_target.label.not_specified.all_target")}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" divider={<Divider />} spacing={1}>
          {ageRangeRows.map((row: AgeRange) => (
            <Stack
              key={row.id}
              direction="row"
              alignItems="center"
              sx={{ justifyContent: "space-between" }}
            >
              <Typography sx={{ color: "text.secondary" }}>
                {t("quest_edit_target.field.basic_attr.age.label", {
                  lower: row.lower,
                  upper: row.upper,
                })}
              </Typography>
              <IconButton onClick={() => handleRemoveAgeRange(row.id)}>
                <ClearIcon />
              </IconButton>
            </Stack>
          ))}
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              placeholder={t(
                "quest_edit_target.field.basic_attr.age.placeholder.lower",
              )}
              value={lower}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLower(e.target.value)
              }
            ></TextField>
            <Typography color="secondary">～</Typography>
            <TextField
              size="small"
              placeholder={t(
                "quest_edit_target.field.basic_attr.age.placeholder.upper",
              )}
              value={upper}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUpper(e.target.value)
              }
            ></TextField>
            <SubActionButton
              onClick={() => handleAddAgeRange()}
            >
              {t("quest_edit_target.button.add")}
            </SubActionButton>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default AgeRangeField;
